/**
 * The clinics list (docs/ux/registry.md "Read-only lists"): the rooms Beam
 * books into, with this week's appointment count, each row opening the
 * clinic page filtered to it. Read only: the clinics come from the seed
 * and, later, from VitalSign.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import type { ClinicRecord } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { ClinicDialog } from '../intake/ListDialogs';
import { useShellWidth } from '@/components/shell/context';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { mondayOf } from '@/features/clinic/describe';
import { longDate } from '@/lib/format';
import { useClinicWeek } from '@/features/clinic/useClinic';
import { RegistryPill } from './RegistryPill';
import { useClinics } from './useRegistry';

export function ClinicsPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const clinics = useClinics();
  const monday = mondayOf(new Date());
  const week = useClinicWeek(monday);
  const thisWeek = new Map<string, number>();
  for (const r of week.data?.rows ?? []) thisWeek.set(r.clinic.id, (thisWeek.get(r.clinic.id) ?? 0) + 1);
  const rows = [...(clinics.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const [dialog, setDialog] = useState<{ open: boolean; existing: ClinicRecord | null }>({ open: false, existing: null });
  const header = (
    <PageHeader
      title="Clinics"
      description={`Where Beam books appointments, with the count for the week of ${longDate(monday.toISOString())}.`}
      tabs={<RegistryPill current="clinics" />}
      actions={<Button className="h-11 md:h-9" onClick={() => setDialog({ open: true, existing: null })}>New clinic</Button>}
    />
  );

  if (clinics.isError) return <div className="flex flex-col gap-3">{header}<ErrorPanel message="The clinics could not be loaded." onRetry={() => void clinics.refetch()} /></div>;
  if (clinics.isPending) return <div className="flex flex-col gap-3">{header}<Skeleton className="h-48 rounded-xl" aria-label="Loading clinics" /></div>;
  if (rows.length === 0) return <div className="flex flex-col gap-3">{header}<EmptyState title="No clinics on the registry yet." description="The seed creates the demo clinics; Beam's rooms arrive with VitalSign." /></div>;

  const count = (c: ClinicRecord) => (week.isPending ? null : thisWeek.get(c.id) ?? 0);
  const clinicHref = (c: ClinicRecord) => `/clinic?clinic=${encodeURIComponent(c.id)}`;

  const dialogEl = <ClinicDialog open={dialog.open} existing={dialog.existing} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />;

  return (
    <div className="flex flex-col gap-3">
      {header}
      {dialogEl}
      {phone ? (
        <ul className="flex flex-col gap-2" data-slot="clinic-cards">
          {rows.map((c) => (
            <li key={c.id} data-slot="clinic-card" data-id={c.id} className="flex flex-col gap-1 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
              <span className="font-medium">{c.name}</span>
              <span className="text-beam-text-muted">{c.suburb}</span>
              <span className="text-beam-text-muted"><span className="tabular">{count(c) ?? '...'}</span> this week</span>
              <Link to={clinicHref(c)} className={buttonVariants({ variant: 'outline', size: 'sm' }) + ' mt-1 self-start'} aria-label={`Open ${c.name} in the clinic`}>Open in clinic</Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-beam-line bg-beam-paper">
          <Table data-slot="clinic-table" className="text-[13px]">
            <caption className="sr-only">Clinics on the registry, by name</caption>
            <TableHeader className="bg-beam-cream-deep">
              <TableRow className="border-beam-line hover:bg-beam-cream-deep">
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Clinic</TableHead>
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Suburb</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[150px] px-4 text-right text-beam-text-soft">This week</TableHead>
                <TableHead scope="col" className="h-9 w-[140px] px-4"><span className="sr-only">Open</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-id={c.id} className="h-[46px] border-beam-line hover:bg-beam-cream-deep">
                  <TableCell className="px-4 py-0 font-medium">{c.name}</TableCell>
                  <TableCell className="px-4 py-0">{c.suburb}</TableCell>
                  <TableCell className="tabular px-4 py-0 text-right" data-slot="week-count">{count(c) ?? ''}</TableCell>
                  <TableCell className="px-4 py-0 text-right"><Link to={clinicHref(c)} className={buttonVariants({ variant: 'outline', size: 'default' })} aria-label={`Open ${c.name} in the clinic`}>Open in clinic</Link></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-[13px] text-beam-text-muted">Doctors are assigned to clinics on their registry page. Appointments are keyed on the case until VitalSign's API arrives.</p>
    </div>
  );
}
